"use client";

import { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { SiteImage } from "@/components/site-image";
import Link from "next/link";
import { ArrowRight, ArrowDown, BookOpen, Search, X } from "lucide-react";
import type { BlogPostSummary } from "@/lib/data/types";
import { blogImageUrl, categoryLabel, formatBlogDate } from "@/lib/api/blog";
import { NewsletterSection } from "@/components/home/newsletter";
import type { BlogDirectoryContent } from "@/lib/api/blog-directory";

const destination = (post: BlogPostSummary) => post.public_url_path || `/blog/${post.slug}`;
function PostImage({ post, featured = false }: { post: BlogPostSummary; featured?: boolean }) {
  const src = blogImageUrl(post.featured_image);
  return <div className="log-image">{src ? <SiteImage src={src} alt={post.title} fill
    sizes={featured ? "(max-width: 760px) 100vw, 55vw" : "(max-width: 600px) 100vw, (max-width: 1000px) 50vw, 33vw"}
    priority={featured} className="log-photo" /> : <BookOpen aria-hidden="true" size={48} />}</div>;
}

export function BlogListContent({ posts, content }: { posts: BlogPostSummary[]; content: BlogDirectoryContent }) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const category = params.get("category") || "all";
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(9);
  const categories = [...new Set(posts.map(p => p.category).filter(Boolean))];
  const filtered = posts.filter(p => (category === "all" || p.category === category)
    && `${p.title} ${p.excerpt || ""} ${p.author || ""} ${categoryLabel(p.category)}`.toLowerCase().includes(query.trim().toLowerCase()));
  const featured = category === "all" && !query.trim() ? filtered[0] : undefined;
  const archive = featured ? filtered.slice(1) : filtered;
  const visible = archive.slice(0, limit);
  function select(value: string) {
    setLimit(9);
    const next = new URLSearchParams(params.toString()); next.delete("page");
    if (value === "all") next.delete("category"); else next.set("category", value);
    router.replace(`${pathname}${next.size ? `?${next}` : ""}`, { scroll: false });
  }
  return <div className="blog-log">
    <section className="log-masthead" aria-labelledby="log-title">
      <SiteImage src={blogImageUrl(content.banner_image) || "/images/blog/the-log-water.png"} alt="" fill priority sizes="100vw" className="log-water" />
      <div className="log-masthead-copy log-width"><p className="log-eyebrow">{content.eyebrow}</p><h1 id="log-title">{content.title}</h1>
        <p className="log-intro">{content.intro}</p>
      </div>
    </section>
    <div className="log-tools"><div className="log-width log-tools-inner">
      <nav className="log-categories" aria-label="Article categories">
        {[{ value: "all", label: "All" }, ...categories.map(value => ({ value, label: categoryLabel(value) }))].map(c =>
          <button key={c.value} type="button" aria-pressed={category === c.value} onClick={() => select(c.value)}>{c.label}</button>)}
      </nav>
      <div className="log-search"><Search size={16} aria-hidden="true" /><input aria-label="Search articles" placeholder="Search articles" type="search" value={query}
        onChange={e => { setQuery(e.target.value); setLimit(9); }} />{query && <button aria-label="Clear search" onClick={() => { setQuery(""); setLimit(9); }}><X size={16} /></button>}</div>
    </div></div>
    {featured && <section className="log-featured" aria-label="Featured article">
      <Link href={destination(featured)} className="log-featured-image" aria-label={featured.title}><PostImage post={featured} featured /></Link>
      <div className="log-featured-copy">
        <div className="log-meta"><span>{categoryLabel(featured.category)}</span><time dateTime={featured.published_at}>{formatBlogDate(featured.published_at)}</time></div>
        <h2><Link href={destination(featured)}>{featured.title}</Link></h2>{featured.excerpt && <p>{featured.excerpt}</p>}
        <div className="log-featured-bottom"><Link className="log-button" href={destination(featured)}>Read the story <ArrowRight size={16} /></Link>{featured.author && <span>By {featured.author}</span>}</div>
      </div>
    </section>}
    <section className="log-archive" aria-label="Articles"><div className="log-width">
      <div className="log-archive-heading">
        <p role="status">Showing {visible.length + (featured ? 1 : 0)} of {filtered.length} articles</p></div>
      {filtered.length === 0 ? <div className="log-empty"><BookOpen size={36} aria-hidden="true" /><h3>{posts.length ? "No matching articles" : "Stories are on their way"}</h3><p>{posts.length ? "Try another search or category." : "Check back for the latest from the marina."}</p>{posts.length > 0 && <button className="log-button" onClick={() => { setQuery(""); select("all"); }}>Show all articles</button>}</div> :
        <div className="log-grid">{visible.map(post => <div className="log-grid-entry" key={post.id}>
          <article className="log-card"><Link href={destination(post)} className="log-card-image" aria-label={post.title}><PostImage post={post} /><span className="log-badge">{categoryLabel(post.category)}</span></Link>
            <div className="log-card-copy"><time dateTime={post.published_at}>{formatBlogDate(post.published_at)}</time><h3><Link href={destination(post)}>{post.title}</Link></h3>{post.excerpt && <p>{post.excerpt}</p>}<Link className="log-read" href={destination(post)}>Read article <ArrowRight size={15} /></Link></div>
          </article>
        </div>)}</div>}
      {visible.length < archive.length && <div className="log-more"><button className="log-button log-button-outline" onClick={() => setLimit(n => n + 9)}>Load more articles <ArrowDown size={16} /></button></div>}
    </div></section>
    <div id="blog-newsletter" className="log-newsletter"><p className="log-eyebrow">{content.newsletter_eyebrow}</p><NewsletterSection title={content.newsletter_title} description={content.newsletter_description} /></div>
  </div>;
}
